import { CamelCasedPropertiesDeep } from 'type-fest';
import { z } from 'zod/v4';
import { colorPaletteKindSchema, unitOptionsSchema } from '../../utils';
import { createDefinitionSchema } from '../../utils/helpers';
import { legendSpecOptionsSchema } from '../../utils/legend';
import { colorOverrideSchema, newOverrideObject } from '../../utils/overrides';

export type DisplayOption = z.infer<typeof displayOptionsSchema>;
export const displayOptionsSchema = z.enum(['Default', 'Donut']);

export interface PieChartVisualOptions extends z.TypeOf<typeof pieChartVisualOptionsSchema> {}
const pieChartVisualOptionsSchema = z.object({
  display: displayOptionsSchema.optional(),
  show_series_name: z.boolean().optional(),
  color_palette: colorPaletteKindSchema.optional(),
});

export type LabelFormatOption = z.infer<typeof labelFormatOptionsSchema>;
export const labelFormatOptions = z.enum(['Percent', 'Value']);
export const labelFormatOptionsSchema = z.array(labelFormatOptions).optional();

/**
 * PieChart-specific override schema. Only color is supported for pie slices.
 */
export interface PieChartVisualOverride extends z.TypeOf<typeof pieChartVisualOverrideSchema> {}
export const pieChartVisualOverrideSchema = z
  .object({
    // Future PieChart specific overrides can be added here
  })
  .merge(colorOverrideSchema);

export interface PieChartOverrides extends z.TypeOf<typeof pieChartOverridesSchema> {}
export const pieChartOverridesSchema = z.array(newOverrideObject(pieChartVisualOverrideSchema));

/**
 * Type for the spec-driven `DashboardPanel` variant of the pie chart.
 */
export interface PieChartSpec extends z.TypeOf<typeof pieChartSpecSchema> {}

/**
 * Zod schema for the spec-driven `DashboardPanel` variant of the pie chart.
 */
export const pieChartSpecSchema = z.object({
  legend: legendSpecOptionsSchema.optional(),
  visual: pieChartVisualOptionsSchema.optional(),
  label_format: labelFormatOptionsSchema.optional(),
  unit: unitOptionsSchema.optional(),
  percent_decimal_places: z.number().optional(),
  aggregation_percent: z.number().optional(),
  overrides: pieChartOverridesSchema.optional(),
});

/**
 * The schema for a PieChart `DashboardPanel` definition..
 */
export interface PieChartDefinition extends z.TypeOf<typeof pieChartDefinitionSchema> {}
export const pieChartDefinitionSchema = createDefinitionSchema('PieChart', pieChartSpecSchema);

/**
 * Version of the Zod schema to be used for base types for the props-driven `Viz` variant of the pie chart.
 */
export const pieChartVizSchema = pieChartSpecSchema
  // Remove visual props and instead use a flattened version of it in the `Viz` use case.
  .omit({
    visual: true,
  })
  // Unwrap the optional visual schema to get the underlying object for merging.
  .merge(pieChartSpecSchema.shape.visual.unwrap());

type PieChartVizSchemaType = z.TypeOf<typeof pieChartVizSchema>;

/**
 * Base type for the props-driven `Viz` variant of the pie chart generated from the Zod schema and
 * converting most snake_case properties to camelCase (exception for `unit`). This interface should
 * be extended in `viz-framework` to do the following:
 * - Add additional types for `Viz` only use cases.
 * - Extend properties to add more meaningful jsdoc. This is necessary because both `z.Typeof` and
 *   `CamelCasedPropertiesDeep` lose jsdoc.
 */
export interface PieChartPropsBase
  extends CamelCasedPropertiesDeep<Omit<PieChartVizSchemaType, 'unit'>>,
    Pick<PieChartVizSchemaType, 'unit'> {}
